import React from 'react';
import PropTypes from 'prop-types';
import { PopoverBody, UncontrolledPopover } from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { formatNumber } from '@techchange/components';

import UserAvatar from '../UserAvatar';

import './UserAvatarList.scss';

const propTypes = {

	/**
	 * The list of users to show.
	 */
	users: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The user id.
		 */
		id: PropTypes.string,

		/**
		 * The display name.
		 */
		display_name: PropTypes.string,

		/**
		 * An array of avatars (profile pictures).
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The image url.
			 */
			image: PropTypes.string,

			/**
			 * The filestack security signature.
			 */
			signature: PropTypes.string,

			/**
			 * The filestack security policy.
			 */
			policy: PropTypes.string,

		})),

	})),

	/**
	 * The current user's id.
	 */
	userId: PropTypes.string,

	/**
	 * The number of users to show.
	 */
	numToShow: PropTypes.number,

	/**
	 * The total number of users.
	 */
	total: PropTypes.number,

	/**
	 * The size of the avatars to render.
	 */
	size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * Flag indicating whether to hide popover of hidden users.
	 */
	hidePopover: PropTypes.bool,

	/**
	 * Flag indicating whether to display users with the stacked styling.
	 */
	stacked: PropTypes.bool,

	/**
	 * Callback to handle additional logic for viewing the user profile modal.
	 */
	onClickUser: PropTypes.func,

};

const defaultProps = {
	total: 0,
	size: '26',
	hidePopover: false,
	stacked: false,
};

const UserAvatarList = (props) => {

	const {
		users,
		userId,
		numToShow,
		total,
		size,
		hidePopover,
		stacked,
		onClickUser
	} = props;

	if (!users || users.length === 0) {
		return null;
	}

	const usersCopy = [...users];

	// Check if the current user is in the list of users to show
	const index = usersCopy.findIndex((user) => user.id === userId);

	if (index > 0) {

		// Re-position the current user so that they show up first
		const [user] = usersCopy.splice(index, 1);

		usersCopy.unshift(user);

	}

	// Calculate the cutoff index, i.e. the last user to show
	const cutoffIndex = numToShow || usersCopy.length;

	const usersToShow = usersCopy.slice(0, cutoffIndex);

	const usersToHide = usersCopy.slice(cutoffIndex);

	// Calculate the number of users that are hidden;
	let numHidden = 0;

	// Check if the total number of users is greater than the number of users provided
	if (total > usersToShow.length) {

		numHidden = total - usersToShow.length;

	} else {

		numHidden = usersToHide.length;

	}

	const popoverID = `user-avatar-list-${uuid()}`;

	return (
		<div className="tc-user-avatar-list-container">
			<div className={`tc-user-avatar-list${stacked ? ' stacked' : ''}`}>
				{usersToShow.map((user) => (
					<UserAvatar
						key={user.id}
						user={user}
						size={size}
						url={user.url}
						rounded
						onClick={onClickUser ? (event) => onClickUser(event, user.id) : null} />
				))}
				{/* Check if we are hiding users */}
				{numHidden > 0 &&
					<div
						id={popoverID}
						className="hidden-count">
						{`+${formatNumber(numHidden)}`}
						{/* Check if we showing the popover */}
						{!hidePopover && usersToHide.length > 0 &&
							<UncontrolledPopover
								className="tc-user-avatar-list-hidden-popover"
								innerClassName="tc-user-avatar-list-hidden-popover-inner"
								trigger="hover click"
								target={popoverID}
								// Add a delay so fade-out doesn't flicker when mousing over popover content
								delay={{ hide: 200 }}>
								<PopoverBody>
									{usersToHide.map((user) => (
										<div
											key={user.id}
											className="d-flex align-items-center mt-1 mb-1">
											<UserAvatar
												user={user}
												size={size}
												url={user.url}
												rounded />
											<span>
												{user.display_name}
											</span>
										</div>
									))}
								</PopoverBody>
							</UncontrolledPopover>
						}
					</div>
				}
			</div>
		</div>
	);

};

UserAvatarList.propTypes = propTypes;
UserAvatarList.defaultProps = defaultProps;

export default UserAvatarList;
